/**
 * ProductReviewPage — admin product-review queue (PIM Wave P3 Task 3).
 *
 * Chrome mirrors `seller-applications/QueueListPage.tsx` (hand-rolled
 * `aria-pressed` status chips, `useInfiniteQuery` w/ 30s refetch, oldest-
 * first, "Load more") layered onto the P0 kit's `AdminPageHeader` +
 * `AdminStates` (the two most recent PIM-wave admin pages — FieldLibraryPage,
 * BindingEditorPage — already use this kit combo; `FilterToolbar` is NOT
 * used here because it is single-select and this queue needs independent
 * status buckets).
 *
 * PHOTOS ARE DESCOPED (no `images` field on the admin `ProductResponse`, no
 * admin image-URL plumbing) and the numeric COUNTS BAR is DESCOPED (no
 * product-status counts endpoint shipped) — both flagged as follow-ups in
 * the task report. Status filter chips remain (they switch the queue view).
 *
 * "Seller"/"Category" columns follow the SAME established convention as
 * `products/ProductListPage.tsx` / `ProductDrawer.tsx`: seller is shown as
 * the joined `seller_business_name` (SCRUM-164 — populated on this same
 * `GET /admin/marketplace/products` list read the queue re-hits), falling
 * back to the abbreviated id (`seller_id.slice(0, 8)`) only on an outer-join
 * miss; category is resolved via the shared `useCategoryTree`. There is no
 * "submitted at" column: the admin `ProductResponse` carries no `created_at`
 * field (also flagged as a follow-up).
 */
import { useCallback, useMemo, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { GPill } from "@garden/ui";
import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";
import { AdminPageHeader, AdminStates } from "@/features/admin/_shared";
import { productStatusTone } from "@/features/admin/components/tone";
import {
  categoryDisplayName,
  useCategoryTree,
} from "@/features/marketplace/useCategoryTree";
import type { AdminProduct } from "@/features/admin/products/api";
import { useReviewProducts } from "@/features/admin/product-review/hooks";
import { ReviewDrawer } from "@/features/admin/product-review/components/ReviewDrawer";

const STATUS_BUCKETS = ["pending_approval", "rejected", "active"] as const;
type StatusBucket = (typeof STATUS_BUCKETS)[number];

const FILTER_LABEL_KEY: Record<StatusBucket, string> = {
  pending_approval: "pim.review.filters.pending",
  rejected: "pim.review.filters.sentBack",
  active: "pim.review.filters.approved",
};

interface ProductRowProps {
  item: AdminProduct;
  categoryName: string;
  onClick: (id: string) => void;
}

function ProductRow({ item, categoryName, onClick }: ProductRowProps): ReactElement {
  const { t } = useTranslation(ADMIN_NS);
  const activate = (): void => onClick(item.id);
  return (
    <tr
      data-testid={`review-row-${item.id}`}
      onClick={activate}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          activate();
        }
      }}
      tabIndex={0}
      role="button"
      aria-label={item.name}
      className="cursor-pointer border-b border-[var(--g-hairline)] hover:bg-[var(--g-elevated)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--g-primary)]"
    >
      <td className="min-h-[44px] px-3 py-3 text-sm font-medium">{item.name}</td>
      <td className="px-3 py-3 text-sm text-[var(--g-secondary)]">
        {item.seller_business_name ?? item.seller_id.slice(0, 8)}
      </td>
      <td className="px-3 py-3 text-sm text-[var(--g-secondary)]">{categoryName}</td>
      <td className="px-3 py-3">
        <GPill tone={productStatusTone(item.status)} size="sm">
          {t(`products.status.${item.status}`, { defaultValue: item.status })}
        </GPill>
      </td>
    </tr>
  );
}

function ProductCard({ item, categoryName, onClick }: ProductRowProps): ReactElement {
  const { t } = useTranslation(ADMIN_NS);
  const activate = (): void => onClick(item.id);
  return (
    <div
      data-testid={`review-card-${item.id}`}
      onClick={activate}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          activate();
        }
      }}
      tabIndex={0}
      role="button"
      aria-label={item.name}
      className="cursor-pointer rounded-[var(--g-radius)] border border-[var(--g-hairline)] bg-[var(--g-surface)] p-4 shadow-[var(--g-shadow-sm)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)]"
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-medium">{item.name}</div>
          <div className="mt-0.5 text-sm text-[var(--g-secondary)]">
            {categoryName} · {item.seller_business_name ?? item.seller_id.slice(0, 8)}
          </div>
        </div>
        <GPill tone={productStatusTone(item.status)} size="sm">
          {t(`products.status.${item.status}`, { defaultValue: item.status })}
        </GPill>
      </div>
    </div>
  );
}

export function ProductReviewPage(): ReactElement {
  registerAdminI18n();
  const { t, i18n } = useTranslation(ADMIN_NS);

  const [status, setStatus] = useState<StatusBucket>("pending_approval");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data, isLoading, isError, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useReviewProducts(status);
  const { findNode } = useCategoryTree();

  const handleSelect = useCallback((id: string) => {
    setSelectedId(id);
  }, []);

  const items: AdminProduct[] = useMemo(
    () => data?.pages.flatMap((p) => p.products) ?? [],
    [data],
  );

  const categoryName = useCallback(
    (id: string): string => {
      const node = findNode(id);
      return node ? categoryDisplayName(node, i18n.language) : id.slice(0, 8);
    },
    [findNode, i18n.language],
  );

  // SCRUM-196: auto-publish (REQ-PRD-§11.14-F) means the `pending_approval`
  // bucket is, by design, permanently empty — a product is rewritten to
  // `active` before it can ever land here. The generic `pim.review.empty`
  // label ("Нет товаров в этой категории") reads as broken in that steady
  // state, so this bucket gets a dedicated explanation instead. The
  // `rejected`/`active` buckets keep the generic empty label — they stay
  // genuinely reachable and can be legitimately empty OR populated.
  const isPendingBucketEmpty =
    status === "pending_approval" && !isLoading && !isError && items.length === 0;

  return (
    <div data-testid="product-review-page" className="flex flex-col gap-4">
      <AdminPageHeader
        title={t("pim.review.title")}
        subtitle={t("pim.review.subtitle")}
      />

      <div
        data-testid="product-review-filters"
        className="flex flex-wrap items-center gap-2"
      >
        {STATUS_BUCKETS.map((bucket) => (
          <button
            key={bucket}
            type="button"
            onClick={() => setStatus(bucket)}
            aria-pressed={status === bucket}
            className={[
              "min-h-[44px] rounded-full px-3 py-1 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)]",
              status === bucket
                ? "bg-[var(--g-primary)] text-white"
                : "bg-[var(--g-elevated)] text-[var(--g-secondary)] hover:bg-[var(--g-hairline)]",
            ].join(" ")}
          >
            {t(FILTER_LABEL_KEY[bucket])}
          </button>
        ))}
      </div>

      <AdminStates
        isLoading={isLoading}
        isError={isError}
        isEmpty={items.length === 0 && status !== "pending_approval"}
        loadingLabel={t("pim.review.title")}
        emptyLabel={t("pim.review.empty")}
        errorLabel={t("pim.review.error")}
        testid="product-review"
      >
        {isPendingBucketEmpty ? (
          <div
            data-testid="product-review-pending-empty"
            className="flex flex-col items-center gap-1 py-8 text-center"
          >
            <p className="text-sm font-medium text-foreground">
              {t("pim.review.emptyPendingTitle")}
            </p>
            <p className="text-sm text-[var(--g-secondary)]">
              {t("pim.review.emptyPendingBody")}
            </p>
          </div>
        ) : (
        <>
          <div className="hidden sm:block overflow-x-auto rounded-[var(--g-radius-md)] border border-[var(--g-hairline)]">
            <table
              data-testid="product-review-table"
              className="w-full min-w-[600px] border-collapse bg-[var(--g-surface)] text-sm"
            >
              <thead className="bg-[var(--g-elevated)]">
                <tr>
                  <th
                    scope="col"
                    className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-[var(--g-secondary)]"
                  >
                    {t("products.columns.name")}
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-[var(--g-secondary)]"
                  >
                    {t("products.columns.seller")}
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-[var(--g-secondary)]"
                  >
                    {t("products.columns.category")}
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-[var(--g-secondary)]"
                  >
                    {t("products.columns.status")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <ProductRow
                    key={item.id}
                    item={item}
                    categoryName={categoryName(item.category_id)}
                    onClick={handleSelect}
                  />
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col gap-2 sm:hidden" data-testid="product-review-cards">
            {items.map((item) => (
              <ProductCard
                key={item.id}
                item={item}
                categoryName={categoryName(item.category_id)}
                onClick={handleSelect}
              />
            ))}
          </div>

          {hasNextPage ? (
            <button
              type="button"
              onClick={() => void fetchNextPage()}
              disabled={isFetchingNextPage}
              className="mx-auto min-h-[44px] rounded-full bg-[var(--g-elevated)] px-6 py-2 text-sm font-medium text-[var(--g-secondary)] hover:bg-[var(--g-hairline)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)] disabled:opacity-50"
            >
              {isFetchingNextPage ? t("common.refreshing") : t("common.loadMore")}
            </button>
          ) : null}
        </>
        )}
      </AdminStates>

      <ReviewDrawer
        productId={selectedId}
        open={selectedId !== null}
        onClose={() => setSelectedId(null)}
      />
    </div>
  );
}

export default ProductReviewPage;
